import { createHeadlessEditor } from '@lexical/headless';
import type { EditorState, SerializedEditorState } from 'lexical';
import { $getRoot } from 'lexical';

import { setDocToScriptPage } from './script-composer-actions';

import type {
    DashboardFlowEnum,
    GenerateScriptFromPromptRequest,
    GenerateScriptRequest,
    MediaCandidates,
    MediaOptions,
    QueuedTaskResult,
    SerializedArticleNode,
    SlideBackgroundModel,
} from 'js/src/client';
import { ApiService, NarrativeThreadEnum, PerspectiveEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { getCanUseAIAvatar, getCanUseAIVoiceover } from 'js/src/data/account';
import {
    avatarBackgroundVideoAsset,
    avatarVideoAsset,
    getAIAvatarVoicePresetByAvatarId,
} from 'js/src/data/ai-voices-and-avatars';
import {
    getSelectedAspectRatio,
    getSelectedBlueprintId,
    getVideoInitType,
    OPTIONS_PAGE_VIDEO_INIT_TYPE,
    segmentText,
    setBlueprintId,
    setDashboardFlow,
    setInitialBlueprintId,
    setIsStoryboardGenerating,
    setVideoInitType,
    VIDEO_INIT_TYPE,
    VIDEO_INIT_TYPE_TO_NARRATIVE_THREAD,
} from 'js/src/data/app';
import { getBlueprint } from 'js/src/data/blueprints';
import { hideModal, LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import {
    setCurrentAIAvatar,
    setCurrentAIAvatarVoicePreset,
    setCurrentAIVoice,
    setDocToScriptData,
    setDocToScriptImportType,
    setDocToScriptOptions,
    setEditedScript,
    setEditedScriptInitialWordCount,
    setEditedScriptLanguage,
    setIdeaOutlineReference,
    setIdeaOutlineSubject,
    setIsConvertingToVoiceover,
    setIsFetchingAIScriptOptions,
    setIsFetchingScriptOptionAIScript,
    setLongestSupportedScriptDuration,
    setScriptForScriptOption,
    setSelectedScriptOption,
} from 'js/src/data/script-composer/script-composer-actions';
import {
    getAIScriptData,
    getCurrentAIAvatar,
    getCurrentAIAvatarVoicePreset,
    getCurrentAIVoiceId,
    getEditedScript,
    getEditedScriptLanguage,
    getEditedScriptSentences,
    getEnforceBlockSegmentation,
} from 'js/src/data/script-composer/script-composer-selectors';
import {
    DASHBOARD_VIDEO_FLOWS_TARGET_PAGE,
    DOC_TO_SCRIPT_PAGE,
    type ScriptComposerData,
    type ScriptOption,
} from 'js/src/data/script-composer/script-composer-types';
import {
    buildBlockSegmentationPrompt,
    DOC_IMPORT_EDITOR_CONFIG,
    getSentencesFromScript,
} from 'js/src/data/script-composer/script-composer-utils';
import { getUsageLimits } from 'js/src/data/usage-limit/usage-limit-selectors';
import { getBrandKitId } from 'js/src/data/video';
import { generateVoiceoverAsset } from 'js/src/data/video-planner/video-planner-utils';
import { $convertLexicalStateToArticle } from 'js/src/libs/article-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    COOKIE_KEYS,
    ImmutableFromJS,
    MAX_SENTENCES_FOR_USE_ALL_SENTENCES,
    SCRIPT_CREATION_SOURCES,
} from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import {
    addSceneGroupFromVoiceoverAssetOperation,
    applyBlueprintTemplateSettingsOperation,
    fetchAndSetRecommendedTextOnMediaVideoOperation,
    fetchRecommendedVoiceDrivenVideoOperation,
} from 'js/src/luminary';
import { applyBrandKitOperation } from 'js/src/luminary/brand-kit-operations';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { getNarrativeThreadFromVideoInitType } from 'js/src/luminary/luminary-utils';
import Cookies from 'js-cookie';

export function generateScript(
    editorText: string,
    videoId: number,
    scriptOption: ScriptOption,
    delay: number,
    narrativeThread: NarrativeThreadEnum,
    templateId: string,
    blueprintId?: number,
): Promise<QueuedTaskResult> {
    let description = scriptOption.description;
    if (scriptOption.include_hook) {
        description += ' Start the script with an attention-grabbing hook.';
    }
    if (scriptOption.include_cta) {
        description += ' End the script with a call to action.';
    }
    return new Promise((resolve, reject) =>
        setTimeout(() => {
            function isPerspective(value?: string): value is PerspectiveEnum {
                if (!value) {
                    return false;
                }
                return Object.values(PerspectiveEnum).includes(value as PerspectiveEnum);
            }
            const body: GenerateScriptRequest = {
                video_id: videoId,
                narrative_thread: narrativeThread,
                title: scriptOption.title,
                doctext: editorText,
                duration: scriptOption.duration_seconds,
                tone: scriptOption.tone,
                perspective: isPerspective(scriptOption.perspective)
                    ? scriptOption.perspective
                    : undefined,
                description,
            };
            if (blueprintId) {
                body.blueprint_id = blueprintId;
                body.template_id = templateId;
            }
            ApiService.generateScriptCreate({ body })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error: unknown) => {
                    reject(error);
                });
        }, delay),
    );
}

/**
 * Updates article with latest script info (original content = user edited script)
 * @param articleId The article ID
 * @param editorState The lexical editor state of the content
 * @param reset specifies whether to use a blank article e.g if the original content is no longer derived from an article
 */
export async function saveArticle(articleId: number, editorState: EditorState, reset = false) {
    let originalContentText = '';
    let serializedScript: Array<SerializedArticleNode> | null = null;
    editorState.read(() => {
        originalContentText = $getRoot().getTextContent();
        serializedScript = $convertLexicalStateToArticle();
    });
    try {
        const response = await ApiService.articlePartialUpdate({
            path: { id: articleId },
            body: {
                original_content: originalContentText
                    ? (editorState.toJSON() as unknown as {
                          [k: string]: unknown;
                      })
                    : undefined,
                serialized_script: serializedScript,
                reset: reset,
            },
        });
        const sentencesResponse = await ApiService.articleSentencesRetrieve({
            path: { id: articleId },
        });
        const sentences = sentencesResponse.data?.sentences ?? [];
        return {
            language: response.data.language,
            sentences: sentences.length > 0 ? sentences : [originalContentText],
        };
    } catch (error) {
        ErrorReporter.capture(error);
        Toast.error('Failed to save article');
    }
}

/**
 * Convert serialized editor state to lexical editor state and store in docToScriptData
 */
export function hydrateOriginalContent(
    url: string,
    serializedOriginalContent: SerializedEditorState,
) {
    return (dispatch: AppDispatch) => {
        if (!serializedOriginalContent?.root?.type) {
            return;
        }
        const editor = createHeadlessEditor(DOC_IMPORT_EDITOR_CONFIG);
        // We switched from using 'block-wrapper-node' to paragraph node but some existing content may still use the
        // block-wrapper-node so since it has the same structure as a paragraph node, we can do a simple search and replace
        const originalContentString = JSON.stringify(serializedOriginalContent).replace(
            /block-wrapper-node/g,
            'paragraph',
        );
        const editorState = editor.parseEditorState(originalContentString);
        dispatch(setDocToScriptData({ url, originalEditorState: editorState }));
    };
}

export function hydrateScriptComposerData(data: ScriptComposerData) {
    return (dispatch: AppDispatch) => {
        const {
            edited_script = null,
            selected_script_option = null,
            script_options = null,
            script_cache = null,
            longest_supported_script_duration = null,
            skip_ai_summarization_stage = null,
            page = null,
            edited_script_initial_word_count = null,
            edited_script_language = null,
            selected_ai_avatar_voice_preset = null,
            selected_ai_voice = null,
            selected_ai_avatar = null,
            import_type = null,
            idea_outline_data = null,
            dashboard_flow = null,
        } = data;

        if (script_cache) {
            Object.keys(script_cache).forEach((scriptOptionId) => {
                dispatch(
                    setScriptForScriptOption({
                        scriptOptionId,
                        scriptData: script_cache[scriptOptionId],
                    }),
                );
                dispatch(
                    setIsFetchingScriptOptionAIScript({
                        scriptOptionId,
                        isLoading: false,
                    }),
                );
            });
        }
        if (script_options) {
            Object.keys(script_options).forEach((scriptId) => {
                dispatch(
                    setDocToScriptOptions({
                        scriptOptions: script_options[scriptId],
                        scriptId,
                    }),
                );
                dispatch(
                    setIsFetchingAIScriptOptions({
                        scriptId,
                        isLoading: false,
                    }),
                );
            });
        }
        if (selected_script_option) {
            dispatch(
                setSelectedScriptOption({
                    scriptOption: selected_script_option,
                }),
            );
        }

        if (longest_supported_script_duration) {
            dispatch(setLongestSupportedScriptDuration(longest_supported_script_duration));
        }

        if (edited_script) {
            dispatch(setEditedScript({ scriptData: edited_script }));
        }

        if (edited_script_initial_word_count) {
            dispatch(
                setEditedScriptInitialWordCount({
                    editedScriptInitialWordCount: edited_script_initial_word_count,
                }),
            );
        }

        if (edited_script_language) {
            dispatch(setEditedScriptLanguage(edited_script_language));
        }

        if (selected_ai_avatar_voice_preset) {
            dispatch(setCurrentAIAvatarVoicePreset(selected_ai_avatar_voice_preset));
        }

        if (selected_ai_voice) {
            dispatch(setCurrentAIVoice(selected_ai_voice));
        }

        if (selected_ai_avatar) {
            dispatch(setCurrentAIAvatar(selected_ai_avatar));
        }

        if (page) {
            dispatch(setDocToScriptPage({ page }));
        }

        if (dashboard_flow) {
            dispatch(setDashboardFlow({ dashboardFlow: dashboard_flow }));
        }

        if (skip_ai_summarization_stage) {
            dispatch(setDocToScriptData({ shouldSkipAISummarization: true }));
        }

        if (import_type) {
            dispatch(setDocToScriptImportType(import_type));
        }

        if (idea_outline_data && idea_outline_data.references) {
            dispatch(setIdeaOutlineSubject(idea_outline_data.subject ?? ''));
            idea_outline_data.references.forEach((reference, index) => {
                dispatch(setIdeaOutlineReference({ index, reference }));
            });
        }
    };
}

export function setCurrentAvatarPresetByAvatarId(avatarId: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        /**
         * In some cases we have the avatar id but not the preset it belongs to so this
         * looks up the preset for the avatar id and sets it as the current preset.
         * NOTE: this assumes each preset has a unique avatar id
         */
        const avatarPreset = getAIAvatarVoicePresetByAvatarId(getState(), avatarId);
        if (avatarPreset) {
            dispatch(setCurrentAIAvatarVoicePreset(avatarPreset.get('id')));
        }
    };
}

export function generateScriptFromPromptRequest(
    videoId: number,
    narrativeThread: NarrativeThreadEnum,
    topic: string,
    reference: string,
    duration: number,
    templateId: string,
    tone?: string,
    instructions?: string,
    blueprintId?: number,
    delay = 0,
): Promise<string> {
    return new Promise((resolve, reject) =>
        setTimeout(() => {
            const body: GenerateScriptFromPromptRequest = {
                video_id: videoId,
                narrative_thread: narrativeThread,
                subject: topic,
                reference_material: reference,
                duration,
                tone,
                other_instructions: instructions,
            };
            if (blueprintId) {
                body.blueprint_id = blueprintId;
                body.template_id = templateId;
            }
            ApiService.generateScriptFromPromptCreate({ body })
                .then((response) => {
                    resolve(response.data.task_id);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        }, delay),
    );
}

/**
 * Internal operation to finish the script composer and create a VOICEDRIVEN video.
 */
const finishScriptComposerAndCreateVoicedrivenVideoOperation = createLuminaryOperation(
    async (
        { dispatch, call, getState },
        {
            mediaCandidates,
            mediaOptions,
        }: {
            mediaCandidates?: MediaCandidates;
            mediaOptions?: MediaOptions;
        },
    ) => {
        const videoInitType = getVideoInitType(getState());
        const avatar = getCurrentAIAvatar(getState());
        const selectedAspectRatio = getSelectedAspectRatio(getState());
        const currentAIVoiceId = getCurrentAIVoiceId(getState());
        if (!currentAIVoiceId) {
            throw new Error('Current AI Voice is required to convert to voiceover');
        }
        const language = getEditedScriptLanguage(getState());
        if (!language) {
            throw new Error('Language is required to convert to voiceover');
        }
        const scriptSentences = getEditedScriptSentences(getState());
        const scriptText = scriptSentences.join(' ');
        dispatch(setIsConvertingToVoiceover(true));
        const processId = uuid();
        const startTime = performance.now();

        const response = await segmentText(scriptSentences, language);
        const words = response.map((segment) => segment.words).flat();
        const narrativeThread = avatar
            ? NarrativeThreadEnum.TALKING_HEAD
            : NarrativeThreadEnum.VOICEOVER;
        const selectedBlueprintId = getSelectedBlueprintId(getState());
        const blockSegmentationPrompt = getEnforceBlockSegmentation(getState())
            ? buildBlockSegmentationPrompt(scriptSentences)
            : null;
        const tasksResultResponse = await call(fetchRecommendedVoiceDrivenVideoOperation, {
            words,
            language,
            narrative_thread: narrativeThread,
            selectedBlueprintId,
            mediaCandidates,
            mediaOptions,
            videoPrompt: blockSegmentationPrompt,
        });

        try {
            analytics.track('Started convert to voiceover', {
                processId,
                language,
                currentAIVoiceId,
                scriptSentences,
            });
            const { voiceoverAsset: audioAsset, transcript } = await generateVoiceoverAsset({
                text: scriptText,
                voice: currentAIVoiceId,
                languageCode: language,
            });
            analytics.track('Finished voiceover upload', {
                processId,
                timeTaken: performance.now() - startTime,
            });

            const avatarAsset: ImmutableMap<SlideBackgroundModel> | null =
                videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR && avatar
                    ? avatarVideoAsset(avatar)
                    : null;
            const avatarBackgroundAssetData = avatarAsset
                ? avatarBackgroundVideoAsset(getCurrentAIAvatarVoicePreset(getState()))
                : null;

            analytics.track('Got avatar asset data', {
                avatar: avatar?.get('name'),
            });

            dispatch(setIsConvertingToVoiceover(false));
            dispatch(hideModal());
            dispatch(setDocToScriptPage({ page: null }));

            if (selectedBlueprintId) {
                call(applyBlueprintTemplateSettingsOperation, selectedBlueprintId);
            } else {
                const brandKitId = getBrandKitId(getState());
                if (brandKitId !== null) {
                    call(applyBrandKitOperation, brandKitId, {
                        systemTriggered: true,
                        skipAnalytics: true,
                    });
                }
            }

            call(addSceneGroupFromVoiceoverAssetOperation, {
                targetIndex: 0,
                audioAssetData: audioAsset,
                avatarAssetData: avatarAsset,
                avatarBackgroundAssetData,
                deleteOtherScenes: true,
                analyticsMetadata: {
                    method: 'AI Voice Script Composer',
                },
                selectedBlueprintId,
                aspectRatioOverride: selectedAspectRatio,
                generateRecommendedVideoResponse: tasksResultResponse,
                isGlobalVideo: true,
                transcript,
                systemTriggered: true,
                parentActionGroup: uuid(),
            });
        } catch (error) {
            dispatch(setIsConvertingToVoiceover(false));
            Toast.error(
                'Sorry, something went wrong while converting to voiceover. Please try again.',
            );
            analytics.track('Error converting to voiceover', {
                processId,
                timeTaken: performance.now() - startTime,
                error,
            });
            ErrorReporter.capture(error);
        }
    },
);

/**
 * Internal operation to finish the script composer and create a TEXT video.
 */
const finishScriptComposerAndCreateTextVideoOperation = createLuminaryOperation(
    async (
        { dispatch, call, getState },
        {
            mediaCandidates,
            mediaOptions,
        }: {
            mediaCandidates?: MediaCandidates;
            mediaOptions?: MediaOptions;
        },
    ) => {
        const { shouldSkipAISummarization } = getAIScriptData(getState());
        const usedAI = !shouldSkipAISummarization;
        const editedScript = getEditedScript(getState());
        if (editedScript === null) throw new Error('No script to generate scenes from');
        const editedScriptLanguage = getEditedScriptLanguage(getState());
        const selectedBlueprintId = getSelectedBlueprintId(getState());
        const selectedAspectRatio = getSelectedAspectRatio(getState());
        let scriptSentences = getSentencesFromScript(editedScript);
        if (scriptSentences.length === 0) {
            throw new Error('No script sentences to generate scenes from');
        }
        dispatch(setIsStoryboardGenerating({ isGenerating: true }));

        const brokeLimit = scriptSentences.length > MAX_SENTENCES_FOR_USE_ALL_SENTENCES;
        if (brokeLimit) {
            // if we broke the number of sentences limit, display a warning to the user
            Toast.info(
                `Hang tight, we're creating the first ${MAX_SENTENCES_FOR_USE_ALL_SENTENCES} scenes from your content. You can find the rest of your content in the Source tab.`,
                '',
            );
            scriptSentences = scriptSentences.slice(0, MAX_SENTENCES_FOR_USE_ALL_SENTENCES);
        } else {
            Toast.info('Generating your video, one moment please.', '');
        }
        dispatch(hideModal());
        dispatch(setDocToScriptPage({ page: null }));

        const creationMethod = usedAI
            ? 'Generated from AI summarized script'
            : 'Generated From Article';

        if (selectedBlueprintId) {
            call(applyBlueprintTemplateSettingsOperation, selectedBlueprintId);
        } else {
            const brandKitId = getBrandKitId(getState());
            if (brandKitId !== null) {
                call(applyBrandKitOperation, brandKitId, {
                    systemTriggered: true,
                    skipAnalytics: true,
                });
            }
        }

        await call(fetchAndSetRecommendedTextOnMediaVideoOperation, {
            scriptSegments: scriptSentences,
            language: editedScriptLanguage,
            creationMethod,
            deleteOtherScenes: true,
            blueprintId: selectedBlueprintId,
            aspectRatioOverride: selectedAspectRatio,
            mediaCandidates,
            mediaOptions,
            systemTriggered: true,
        });
    },
);

export const initializeVideoInitType = (
    videoInitType: OPTIONS_PAGE_VIDEO_INIT_TYPE,
    blueprintId: number | null,
) => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const usageLimits = getUsageLimits(getState());
        const hasHitOrExceededAIVODurationLimit =
            usageLimits &&
            usageLimits.ai_vo_minutes_limit_per_cycle &&
            usageLimits.ai_vo_minutes_used_in_cycle >= usageLimits.ai_vo_minutes_limit_per_cycle;
        const isUsingBlueprints = blueprintId !== null;
        if (isUsingBlueprints) {
            // the initial blueprint id tracks the blueprint that the user initially selected when starting the script composer
            dispatch(setInitialBlueprintId({ blueprintId }));
        }
        const blueprint = getBlueprint(getState(), blueprintId);
        const blueprintNarrativeThread = blueprint?.narrative_thread;

        const canUseAIVoiceover = getCanUseAIVoiceover(getState());
        const canUseAIAvatar = getCanUseAIAvatar(getState());

        if (videoInitType === OPTIONS_PAGE_VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER) {
            if (!canUseAIVoiceover) {
                Toast.error('AI Voiceover is not currently available for your account');
                return false;
            }
            if (hasHitOrExceededAIVODurationLimit) {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: ImmutableFromJS({
                            featureName: LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT,
                            subCategory: 'Script composer option selection',
                        }),
                    }),
                );
            }
        } else if (videoInitType === OPTIONS_PAGE_VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR) {
            if (!canUseAIAvatar) {
                Toast.error('AI Avatar is not currently available for your account');
                return false;
            }
            if (hasHitOrExceededAIVODurationLimit) {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: ImmutableFromJS({
                            featureName: LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT,
                            subCategory: 'Script composer option selection',
                        }),
                    }),
                );
            }
        }
        if (
            isUsingBlueprints &&
            VIDEO_INIT_TYPE_TO_NARRATIVE_THREAD[videoInitType] !== blueprintNarrativeThread
        ) {
            dispatch(setBlueprintId({ blueprintId: null }));
        }
        // Initialize script composer with cached source from cookies if it exists (not supported with blueprints currently)
        if (!isUsingBlueprints) {
            const lastCachedScriptSource = Cookies.get(
                COOKIE_KEYS.LAST_SELECTED_SCRIPT_SOURCE,
            ) as SCRIPT_CREATION_SOURCES;
            if (Object.values(SCRIPT_CREATION_SOURCES).includes(lastCachedScriptSource)) {
                dispatch(setDocToScriptImportType(lastCachedScriptSource));
            }
        }
        dispatch(setVideoInitType(videoInitType));
        analytics.track('Option selection stage completed', {
            option: videoInitType,
            isUsingBlueprints,
        });
        return true;
    };
};

/**
 * Once we've completed all the steps (and the state has been set) in the script composer,
 * call this operation to generate the VO (if necessary) and create the video.
 */
export const finishScriptComposerAndCreateVideoOperation = createLuminaryOperation(
    async (
        { call, getState },
        {
            mediaCandidates,
            mediaOptions,
        }: {
            mediaCandidates?: MediaCandidates;
            mediaOptions?: MediaOptions;
        },
    ) => {
        const videoInitType = getVideoInitType(getState());
        const narrativeThread = getNarrativeThreadFromVideoInitType(videoInitType);
        if (narrativeThread === NarrativeThreadEnum.TEXT) {
            await call(finishScriptComposerAndCreateTextVideoOperation, {
                mediaCandidates,
                mediaOptions,
            });
        } else {
            await call(finishScriptComposerAndCreateVoicedrivenVideoOperation, {
                mediaCandidates,
                mediaOptions,
            });
        }
    },
);

export function getScriptComposerStartPageFromFlow(flow: DashboardFlowEnum | null) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const selected_blueprint = getBlueprint(getState(), getSelectedBlueprintId(getState()));
        const hasTargetPage = flow && Object.keys(DASHBOARD_VIDEO_FLOWS_TARGET_PAGE).includes(flow);
        if (selected_blueprint && !hasTargetPage) {
            return DOC_TO_SCRIPT_PAGE.INITIAL_SCRIPT;
        }

        if (!flow) {
            return DOC_TO_SCRIPT_PAGE.OPTION_SELECTION;
        }
        return hasTargetPage
            ? DASHBOARD_VIDEO_FLOWS_TARGET_PAGE[flow]
            : DOC_TO_SCRIPT_PAGE.OPTION_SELECTION;
    };
}
